import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { EntityType } from "src/app/enums/entity-type.enum";
import { ApiService } from "src/app/services/api.service";
import { GaService } from "src/app/services/ga.service";
import { LanguageService } from "src/app/services/language.service";
import { RoutingService } from "src/app/services/routing.service";
import { TranslateService } from "@ngx-translate/core";
import { ARCGIS_MAP_LIFECYCLE } from "../arcgis-map/constants/map-life-cycle.interface";
import { ControlType } from "../arcgis-map/constants/control-type.enum";
import { MapControlItem } from "../arcgis-map/constants/map-control-item.interface";
import { MainMapService } from "../arcgis-map/services/main-map.service";

interface ConservationShapeEntity {
    entityId: number;
    entityType: string;
}

interface ConservationMapShape {
    shapeId: number;
    shapeWkt: string;
    shapeData: {
        entity: ConservationShapeEntity;
    };
}

interface MiniConservationDocumentView {
    documentLongId: string;
    documentTitle: string;
    entityId: number;
}

interface MiniConservationFolderView {
    folderLongId: string;
    folderTitle: string;
    entityId: number;
}

interface ConservationArchiveFolderView {
    entityId: number;
    folderLongId: string;
    archiveCode: string;
    folderNumber: string;
    folderTitle: string;
    siteId: number | null;
    siteSubId: number | null;
    boxId: number | null;
    siteEntityId: number;
    siteName: string;
    siteType: string;
    totalDocuments: number | null;
    folderHasScannedFiles: boolean;
    folderDownloadUrl: string;
    documents: MiniConservationDocumentView[];
    additionalFolders: MiniConservationFolderView[];
    archiveMapInformation: ConservationMapShape[];
}

@Component({
    selector: 'app-conservation-archive-folder',
    templateUrl: './conservation-archive-folder.component.html',
    styleUrls: ['./conservation-archive-folder.component.scss'],
    providers: [
        {
            provide: ARCGIS_MAP_LIFECYCLE,
            useClass: MainMapService,
        },
    ],
})
export class ConservationArchiveFolderComponent implements OnInit, OnDestroy {
    closeClicked: Subject<void> = new Subject<void>();
    currentLanguage$!: Observable<string>;
    currentLang = 'he';

    folderDetails!: ConservationArchiveFolderView;
    folderDetailsLoading = true;
    entityId = 0;

    shapes: any[] = [];
    mapControls: MapControlItem[] = [
        { position: 'top-left', type: ControlType.FullScreen },
        { position: 'top-left', type: ControlType.Zoom },
        {
            position: 'top-left',
            type: ControlType.BasemapGallery,
            expand: {
                id: 'basemap-gallery-expand',
                position: 'top-left',
                expandIcon: 'basemap',
                expandTooltip: '',
                mode: 'floating',
            },
        },
        {
            position: 'top-left',
            type: ControlType.Locate,
            attributes: { scale: 5000 },
        },
    ];

    private queryParamsSubscription?: Subscription;
    private languageSubscription?: Subscription;

    constructor(
        private api: ApiService,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private routingService: RoutingService,
        private languageService: LanguageService,
        private gaService: GaService,
    ) {}

    ngOnInit(): void {
        this.currentLanguage$ = this.languageService.currentLanguage$;
        this.languageSubscription = this.currentLanguage$.subscribe((lang) => {
            this.currentLang = lang;
        });

        this.queryParamsSubscription = this.route.queryParamMap.subscribe((params) => {
            const entityId = Number(params.get('entityId')) || 0;
            if (!entityId || entityId === this.entityId) {
                return;
            }
            this.entityId = entityId;
            this.getFolderDetails(entityId);
        });
    }

    private getFolderDetails(entityId: number): void {
        this.folderDetailsLoading = true;
        this.api
            .getConservationArchiveFolder(entityId)
            .pipe(takeUntil(this.closeClicked))
            .subscribe({
                next: (data: ConservationArchiveFolderView) => {
                    this.folderDetails = data;
                    this.shapes = (data.archiveMapInformation || []).map((shape) => this.convertToConvertedShape(shape));
                    this.folderDetailsLoading = false;
                },
                error: () => {
                    this.folderDetailsLoading = false;
                },
                complete: () => {
                    this.folderDetailsLoading = false;
                },
            });
    }

    private convertToConvertedShape(shape: ConservationMapShape): any {
        const entity = shape.shapeData.entity;
        return {
            shapeId: shape.shapeId.toString(),
            wkt: shape.shapeWkt,
            layerName: "שכבת תוצאות שאילתה",
            hidden: false,
            isExtent: true,
            shapeData: {
                entity: {
                    entityId: entity.entityId,
                    entityType: entity.entityType,
                },
            },
            styledParams: {
                geometryType: "Polygon",
                fillColor: [255, 165, 0, 0.2],
                borderColor: [255, 165, 0],
                borderWidth: 2,
            },
        };
    }

    onViewFolder(entityId: number): void {
        this.gaService.trackWithGA(EntityType.CONSERVATION_ARCHIVE_FOLDERS, 'entityType');
        this.routingService.navigateToChild({ entityId, entityType: EntityType.CONSERVATION_ARCHIVE_FOLDERS }, 'folders:' + entityId);
    }

    onViewDocument(entityId: number): void {
        this.gaService.trackWithGA(EntityType.CONSERVATION_ARCHIVE_DOCUMENTS, 'entityType');
        this.routingService.navigateToChild({ entityId, entityType: EntityType.CONSERVATION_ARCHIVE_DOCUMENTS }, 'documents:' + entityId);
    }

    onViewSite(entityId: number): void {
        if (!entityId) {
            return;
        }
        this.gaService.trackWithGA(EntityType.HACHRAZA, 'entityType');
        this.routingService.navigateWithMerge({ entityId, entityType: EntityType.HACHRAZA }, ['']);
    }

    onDownload(): void {
        this.handleZip();
    }

    handleZip() {
        this.downloadFile(this.folderDetails?.folderLongId || 'no_name');
    }

    private downloadFile(filename: string) {
        const link = document.createElement('a');
        link.href = this.folderDetails?.folderDownloadUrl || '';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    getDetailsTitle(): string {
        const prefix = this.translate.instant('iaa.conservationArchive.folder.titlePrefix');
        const title = this.folderDetails?.folderLongId;
        return title ? `${prefix} - ${title}` : prefix;
    }

    ngOnDestroy(): void {
        this.queryParamsSubscription?.unsubscribe();
        this.languageSubscription?.unsubscribe();
    }
}
